import { signMedia } from '@/api/aiMarking.api';
import { FF_AI_ASSET_MARKING, FF_AI_ASSET_MARKING_SIGN } from '@/consts/featureFlags';
import { log } from '@/logger/logger';
import { posthog } from '@/providers/posthog';

const logger = log.getLogger('markRenderedPicture');

/**
 * Largest picture sent for signing; a bigger one downloads unsigned and is logged.
 * KEEP_IN_SYNC weavy/core/src/modules/recipe/services/interfaces/media-export.service.interface.ts (MAX_MEDIA_SIGN_BYTES)
 */
const MAX_SIGNED_PICTURE_BYTES = 20 * 1024 * 1024;

/** What to download: the browser's own picture by URL, or the server-signed copy. */
export type MarkedPicture = { url: string } | { blob: Blob };

/** Decodes a `data:image/png;base64,…` URL without a fetch round trip. */
export function pngDataUrlToBlob(dataUrl: string): Blob {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  return new Blob([bytes], { type: 'image/png' });
}

/**
 * With marking on, sends the picture. When signing is on too, returns the signed copy, or the local picture
 * on 204, for a picture over 20 MB, or on any failure. In observe mode it returns the local picture without
 * waiting for the server. Never throws. The server judges the node against the workflow as last saved.
 */
export async function markRenderedPicture(params: {
  workflowId: string;
  nodeId: string;
  dataUrl: string;
}): Promise<MarkedPicture> {
  const { workflowId, nodeId, dataUrl } = params;
  if (!posthog.isFeatureEnabled(FF_AI_ASSET_MARKING)) return { url: dataUrl };
  try {
    const png = pngDataUrlToBlob(dataUrl);
    if (png.size > MAX_SIGNED_PICTURE_BYTES) {
      logger.warn('Picture too large to sign; downloading it unsigned', { workflowId, nodeId, bytes: png.size });
      return { url: dataUrl };
    }
    if (!posthog.isFeatureEnabled(FF_AI_ASSET_MARKING_SIGN)) {
      void signMedia(workflowId, nodeId, png).catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        logger.warn('Could not send the rendered picture to observe it', { workflowId, nodeId, message });
      });
      return { url: dataUrl };
    }
    const signed = await signMedia(workflowId, nodeId, png);
    return signed ? { blob: signed } : { url: dataUrl };
  } catch (error) {
    logger.warn('Could not sign the rendered picture; downloading it unsigned', error);
    return { url: dataUrl };
  }
}
